import React from 'react';
import { formatDate } from '../utils/dateConverter';
import { formatHhmm } from '../utils/reportTime';
import { NO_TICKET_KEY, formatHours } from '../utils/projectPeriodStats';

const MESSAGES = {
    loading: 'Buchungen werden geladen…',
    error: 'Die Buchungen konnten nicht geladen werden.',
    incomplete: 'Bitte Start- und Enddatum wählen.',
    reversed: 'Das Enddatum liegt vor dem Startdatum.',
};

const share = (part, whole) => (whole > 0 ? `${Math.round((part / whole) * 100)} %` : '—');

function Kpi({ label, value, sub }) {
    return (
        <div className="pd-hours-kpi">
            <span className="pd-hours-kpi-label">{label}</span>
            <span className="pd-hours-kpi-value">{value}</span>
            <span className="pd-period-sub">{sub}</span>
        </div>
    );
}

/** Hours booked in the selected period, per ticket and per person; `range` is the one `summary` was loaded for. */
export default function ProjectPeriodSummary({ status, range, summary }) {
    const label = range?.startDate && range?.endDate
        ? `${formatDate(range.startDate)} – ${formatDate(range.endDate)}`
        : '';
    const ready = status === 'done' && summary;

    return (
        <section className="pd-hours-card pd-period" aria-label="Stunden im Zeitraum">
            <div className="pd-period-head">
                <h4 className="pd-hours-title">Stunden im Zeitraum</h4>
                {label && <span className="pd-period-range" data-testid="period-range">{label}</span>}
            </div>

            {!ready && <p className="pd-period-msg">{MESSAGES[status] || MESSAGES.loading}</p>}

            {ready && summary.tickets.length === 0 && (
                <p className="pd-period-msg">Keine Buchungen in diesem Zeitraum.</p>
            )}

            {ready && summary.tickets.length > 0 && (
                <>
                    <div className="pd-hours-kpis">
                        <Kpi label="Stunden" value={formatHours(summary.minutes)}
                             sub={`${formatHhmm(summary.minutes)} Std.`} />
                        <Kpi label="Anfahrt" value={formatHours(summary.travelMinutes)}
                             sub="nicht in den Stunden enthalten" />
                        <Kpi label="Personentage" value={summary.personDays}
                             sub={`davon ${summary.onSiteDays} vor Ort`} />
                    </div>

                    <div className="pd-period-tables">
                        <div className="pd-period-table-wrap">
                            <table className="pd-hours-table" aria-label="Stunden nach Ticket">
                                <thead>
                                    <tr>
                                        <th>Ticket</th>
                                        <th className="pd-num">Stunden</th>
                                        <th className="pd-num">Anteil</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {summary.tickets.map(t => (
                                        <tr key={t.key}>
                                            {t.key === NO_TICKET_KEY
                                                ? <td className="pd-period-none">Ohne Ticket</td>
                                                : <td>{t.title}</td>}
                                            <td className="pd-num">{formatHours(t.minutes)}</td>
                                            <td className="pd-num">{share(t.minutes, summary.minutes)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td>Summe</td>
                                        <td className="pd-num">{formatHours(summary.minutes)}</td>
                                        <td />
                                    </tr>
                                </tfoot>
                            </table>
                        </div>

                        <div className="pd-period-table-wrap">
                            <table className="pd-hours-table" aria-label="Stunden nach Mitarbeiter">
                                <thead>
                                    <tr>
                                        <th>Mitarbeiter</th>
                                        <th className="pd-num">Tage</th>
                                        <th className="pd-num">Stunden</th>
                                        <th className="pd-num">Anfahrt</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {summary.users.map(u => (
                                        <tr key={u.userId}>
                                            <td>
                                                {u.name}
                                                {u.onSiteDays > 0 && (
                                                    <> <span className="pd-period-sub">
                                                        {u.onSiteDays} {u.onSiteDays === 1 ? 'Tag' : 'Tage'} vor Ort
                                                    </span></>
                                                )}
                                            </td>
                                            <td className="pd-num">{u.days}</td>
                                            <td className="pd-num">{formatHours(u.minutes)}</td>
                                            <td className="pd-num">{formatHours(u.travelMinutes)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                                <tfoot>
                                    <tr>
                                        <td>Summe</td>
                                        <td className="pd-num">{summary.personDays}</td>
                                        <td className="pd-num">{formatHours(summary.minutes)}</td>
                                        <td className="pd-num">{formatHours(summary.travelMinutes)}</td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </div>
                </>
            )}
        </section>
    );
}
